import React, { useMemo, useState, useEffect, useRef } from "react";
import { gql, useQuery, useMutation } from "urql";
import { Link } from "wouter";
import { uniq, cloneDeep } from "lodash";
import { Button } from "react-bootstrap";

import { Page } from "../../../components/src/Page";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { twoDecPriceFormat, priceFormat } from "../../../common/src/utils/FormatFunctions";
import { DateForm } from "../../../components/src/dateForm";
import { useQueryState } from "../../../components/src/use-query-state";
import { InstrumentModelTypeEnum } from "../types.generated";
import { NumberField, SubmitButton } from "../../../components/src/form";
import { formikUrqlErrorFormater } from "../../../components/src/common/formik-urql-error-helper";
import { Formik, Form } from "formik";
import { DateHelper } from "../../../common/src/calendar/DateHelper";

const GET_SWAPTIONS = gql`
    fragment legFields on InstrumentModelLegsItem {
        _t
        calendars
        currency
        dayAdjustment
        payLeg
        expiry
        notional
        strike
        volatility
        underlyingId
        dayCount
        fixingType
        flatAdjustment
        longStub
        maturityDate
        maturityPeriod
        multiplier
        rate
        rateIndexId
        recoveryRate
        relativeAdjustment
        rollingDate
        rollingPeriod
        settlementType
        spread
        startDate
        startDateOffset
        startPeriod
        ultimateForwardRate
        zeroFloor
        call
        exercise
        issuerId
        cashFlows {
            notional
            exDate
            payDate
            endDate
            startDate
            fixings {
                date
                rate
                startDate
                endDate
            }
        }
        underlyingInstrument {
            _id
            name
            px(date: $dateString) {
                date
                value
            }
        }
    }
    query getSwaptions($filter: PositionFilter, $dateString: GraphQLDateString) {
        tradingmanager(filter: $filter, filterZeroPositions: true, lookThrough: false) {
            instrumentId
            currency
            exposure
            valuationPrice
            interestRateYieldDelta(date: $dateString)
            clientId
            client {
                _id
                name
            }
            instrument {
                _id
                name
                longName
                modelType
                model {
                    _t
                    quoteCurrency
                    quoteType
                    notionalScaling
                    accountId
                    clientId
                    objectId
                    objectType
                    expiryDate
                    startDate
                    legs {
                        ...legFields
                    }
                }
            }
        }
    }
`;

const GET_RECONCILE_EXPOSURE_COLLATERAL = gql`
    query reconcileExposureCollateral($clientIds: [GraphQLObjectId!]!, $date: GraphQLDateString!) {
        reconcileExposureCollateral(clientIds: $clientIds, date: $date) {
            externalAccountId
            externalAccount {
                name
            }
            clientId
            instrumentId
            counterpartyExposure
        }
    }
`;

const UPDATE_INSTRUMENT = gql`
    mutation updateInstrument($input: UpdateInstrumentInput!) {
        updateInstrument(input: $input) {
            _id
            name
            model {
                _t
                legs {
                    _t
                    volatility
                }
            }
        }
    }
`;

interface SwaptionRow {
    instrumentId: string;
    name: string;
    longName: string | null;
    clientId: string | null;
    clientName: string | null;
    currency: string;
    notional: number | null;
    strike: number | null;
    expiry: string | null;
    volatility: number | null;
    premium: number | null;
    valuation: number | null;
    underlyingInstrumentId: string | null;
    underlyingInstrumentName: string | null;
    underlyingValue: number | null;
    interestRateYieldDelta: number | null;
    counterpartyExposure: number | null | "Missing report";
    counterpartyName: string | null;
    cptyDiff: number | null;
    // Store full instrument and position data for updates
    instrument: any;
    position: any;
}

const getPreviousBusinessDay = (date: Date): Date => {
    const day = date.getDay(); // 0 = Sunday, 6 = Saturday
    if (day === 0) return DateHelper.addDays(date, -2); // Sunday → Friday
    if (day === 1) return DateHelper.addDays(date, -3); // Monday → Friday
    if (day === 6) return DateHelper.addDays(date, -1); // Saturday → Friday
    return DateHelper.addDays(date, -1); // Default: step back 1 day
};

export const SwaptionsPage = (): React.ReactElement => {
    const [date] = useQueryState("date", getPreviousBusinessDay(new Date()).toISOString().slice(0, 10));
    const [filter, setFilter] = useState<any>(null);
    const [volatilityEdit, setVolatilityEdit] = useState<{
        instrumentId: string;
        oldVolatility: number | null;
        instrument: any;
        instrumentName: string;
    } | null>(null);
    const [saveMessage, setSaveMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [updatedInstrumentIds, setUpdatedInstrumentIds] = useState<Set<string>>(new Set());
    const [modalPosition, setModalPosition] = useState<{ x: number; y: number } | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (date) {
            setFilter({ endDate: date });
        }
    }, [date]);

    // Reset modal position when it opens
    useEffect(() => {
        if (volatilityEdit !== null && modalPosition === null) {
            // Center the modal initially
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const modalWidth = 280; // Default modal width
            const modalHeight = 400; // Approximate modal height
            setModalPosition({
                x: (viewportWidth - modalWidth) / 2,
                y: (viewportHeight - modalHeight) / 2
            });
        } else if (volatilityEdit === null) {
            setModalPosition(null);
        }
    }, [volatilityEdit, modalPosition]);

    // Handle mouse move for dragging
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging && dragOffset && modalPosition && modalRef.current) {
                const rect = modalRef.current.getBoundingClientRect();
                const modalWidth = rect.width;
                const modalHeight = rect.height;
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;

                // Calculate new position in viewport coordinates
                let newX = e.clientX - dragOffset.x;
                let newY = e.clientY - dragOffset.y;

                // Constrain to viewport bounds (left is relative to overlay which starts at viewport x=0)
                newX = Math.max(0, Math.min(newX, viewportWidth - modalWidth));
                newY = Math.max(0, Math.min(newY, viewportHeight - modalHeight));

                setModalPosition({
                    x: newX,
                    y: newY
                });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            setDragOffset(null);
        };

        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
            };
        }
    }, [isDragging, dragOffset, modalPosition]);

    const handleDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!modalRef.current) return;
        const rect = modalRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
        setIsDragging(true);
    };

    const [{ fetching, error, data }] = useQuery({
        query: GET_SWAPTIONS,
        variables: { filter, dateString: date },
        pause: !filter,
        requestPolicy: "cache-and-network"
    });

    // Get unique clientIds from swaptions data
    const clientIds = useMemo(() => {
        if (!data || !data.tradingmanager) return [];
        const swaptionPositions = data.tradingmanager.filter(
            (position) => position.instrument?.modelType === InstrumentModelTypeEnum.Swaption
        );
        return uniq(swaptionPositions.map((p) => p.clientId).filter((id) => id));
    }, [data]);

    const [{ fetching: fetchingReconcile, data: reconcileData }] = useQuery({
        query: GET_RECONCILE_EXPOSURE_COLLATERAL,
        variables: { clientIds, date },
        pause: !clientIds.length || !date,
        requestPolicy: "cache-and-network"
    });

    const [_, updateInstrumentMutation] = useMutation(UPDATE_INSTRUMENT);

    // Create a map of reconcile data by instrumentId and clientId
    const reconcileDataMap = useMemo(() => {
        if (!reconcileData || !reconcileData.reconcileExposureCollateral) return {};
        const map: Record<string, { exposure: number | null | "Missing report"; counterpartyName: string | null }> = {};
        reconcileData.reconcileExposureCollateral.forEach((item: any) => {
            if (item.instrumentId && item.clientId) {
                // Convert to string to ensure consistent key matching
                const instrumentId = String(item.instrumentId);
                const clientId = String(item.clientId);
                const key = `${instrumentId}_${clientId}`;
                map[key] = {
                    exposure: item.counterpartyExposure === 0 ? "Missing report" : (item.counterpartyExposure ?? null),
                    counterpartyName: item.externalAccount?.name || null
                };
            }
        });
        return map;
    }, [reconcileData]);

    const swaptionRows: SwaptionRow[] = useMemo(() => {
        if (!data || !data.tradingmanager) return [];

        return data.tradingmanager
            .filter((position) => position.instrument?.modelType === InstrumentModelTypeEnum.Swaption)
            .map((position) => {
                const swaptionLeg = position.instrument?.model?.legs?.find((leg) => leg._t === "SwaptionLeg");
                const fixedAmountLeg = position.instrument?.model?.legs?.find((leg) => leg._t === "FixedAmountLeg");

                // Calculate notional: take notional directly from SwaptionLeg
                let notional: number | null = null;
                if (swaptionLeg?.notional !== null && swaptionLeg?.notional !== undefined) {
                    notional = swaptionLeg.notional;
                }

                // Get premium from FixedAmountLeg's first cashFlow
                let premium: number | null = null;
                if (fixedAmountLeg?.cashFlows && fixedAmountLeg.cashFlows.length > 0) {
                    const firstCashFlow = fixedAmountLeg.cashFlows[0];
                    if (firstCashFlow?.notional !== null && firstCashFlow?.notional !== undefined) {
                        premium = firstCashFlow.notional;
                    }
                }

                // Get valuation from tradingmanager exposure (which is the valuation in base currency)
                // or use valuationPrice if available
                let valuation: number | null = null;
                if (position.exposure !== null && position.exposure !== undefined) {
                    valuation = position.exposure;
                } else if (position.valuationPrice !== null && position.valuationPrice !== undefined) {
                    // Fallback to valuationPrice if exposure not available
                    valuation = position.valuationPrice;
                }

                // Get underlying instrument time series value
                let underlyingValue: number | null = null;
                if (swaptionLeg?.underlyingInstrument?.px?.value !== null && swaptionLeg?.underlyingInstrument?.px?.value !== undefined) {
                    underlyingValue = swaptionLeg.underlyingInstrument.px.value;
                }

                // Get counterparty exposure and name from reconcile data
                // Convert to string to ensure consistent key matching
                const instrumentId = String(position.instrumentId || position.instrument?._id || "");
                const clientId = position.clientId ? String(position.clientId) : null;
                const reconcileKey = clientId ? `${instrumentId}_${clientId}` : null;
                const reconcileInfo = reconcileKey ? reconcileDataMap[reconcileKey] : null;
                const counterpartyExposure = reconcileInfo?.exposure ?? null;
                const counterpartyName = reconcileInfo?.counterpartyName ?? null;

                // Calculate Cpty diff: Valuation minus Counterparty valuation (using negated display value)
                let cptyDiff: number | null = null;
                if (valuation !== null && counterpartyExposure !== null && counterpartyExposure !== "Missing report") {
                    // Use negated counterparty valuation (the display value): valuation - (-counterpartyExposure) = valuation + counterpartyExposure
                    cptyDiff = valuation + counterpartyExposure;
                }

                return {
                    instrumentId: position.instrumentId || position.instrument?._id || "",
                    name: position.instrument?.name || "",
                    longName: position.instrument?.longName || null,
                    clientId: position.clientId || null,
                    clientName: position.client?.name || null,
                    currency: position.currency || "",
                    notional: notional,
                    strike: swaptionLeg?.strike ?? null,
                    expiry: swaptionLeg?.expiry ?? null,
                    volatility: swaptionLeg?.volatility ?? null,
                    premium: premium,
                    valuation: valuation,
                    underlyingInstrumentId: swaptionLeg?.underlyingInstrument?._id || swaptionLeg?.underlyingId || null,
                    underlyingInstrumentName: swaptionLeg?.underlyingInstrument?.name || null,
                    underlyingValue: underlyingValue,
                    interestRateYieldDelta: position.interestRateYieldDelta ?? null,
                    counterpartyExposure: counterpartyExposure,
                    counterpartyName: counterpartyName,
                    cptyDiff: cptyDiff,
                    instrument: position.instrument,
                    position: position
                };
            });
    }, [data, reconcileDataMap]);

    const columns = useMemo(
        () => [
            {
                header: "Client",
                accessorKey: "clientName",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    const clientId = row.original.clientId;
                    const clientName = cellProps.getValue();
                    if (clientId && clientName) {
                        return <Link to={`/parties/${clientId}`}>{clientName}</Link>;
                    }
                    return clientName || "N/A";
                }
            },
            {
                header: "Name",
                accessorKey: "name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <Link to={`/instruments/${row.original.instrumentId}`} target="_blank">
                            {cellProps.getValue()}
                        </Link>
                    );
                }
            },
            {
                header: "Underlying",
                accessorKey: "underlyingInstrumentName",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    const underlyingId = row.original.underlyingInstrumentId;
                    const underlyingName = cellProps.getValue();
                    if (underlyingId && underlyingName) {
                        return (
                            <Link to={`/instruments/${underlyingId}`} target="_blank">
                                {underlyingName}
                            </Link>
                        );
                    }
                    return underlyingName || "N/A";
                }
            },
            {
                header: "Notional",
                accessorKey: "notional",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div style={{ textAlign: "right" }}>{value !== null ? twoDecPriceFormat(value) : "N/A"}</div>;
                }
            },
            {
                header: "Strike",
                accessorKey: "strike",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div style={{ textAlign: "right" }}>{value !== null ? priceFormat(value) : "N/A"}</div>;
                }
            },
            {
                header: "Underlying Value",
                accessorKey: "underlyingValue",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div style={{ textAlign: "right" }}>{value !== null ? priceFormat(value) : "N/A"}</div>;
                }
            },
            {
                header: "Expiry Date",
                accessorKey: "expiry",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return value || "N/A";
                }
            },
            {
                header: "Volatility",
                accessorKey: "volatility",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    const value = cellProps.getValue();
                    const isUpdated = updatedInstrumentIds.has(row.original.instrumentId);
                    if (value === null) {
                        return <div style={{ textAlign: "right" }}>N/A</div>;
                    }
                    return (
                        <div style={{ textAlign: "right" }}>
                            <button
                                onClick={() => {
                                    setVolatilityEdit({
                                        instrumentId: row.original.instrumentId,
                                        oldVolatility: value,
                                        instrument: row.original.instrument,
                                        instrumentName: row.original.name
                                    });
                                }}
                                style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    color: isUpdated ? "#ff8c00" : "inherit",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontSize: "inherit"
                                }}
                            >
                                {priceFormat(value)}
                            </button>
                        </div>
                    );
                }
            },
            {
                header: "Premium",
                accessorKey: "premium",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div style={{ textAlign: "right" }}>{value !== null ? twoDecPriceFormat(value) : "N/A"}</div>;
                }
            },
            {
                header: "Valuation",
                accessorKey: "valuation",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div style={{ textAlign: "right" }}>{value !== null ? twoDecPriceFormat(value) : "N/A"}</div>;
                }
            },
            {
                header: "Delta",
                accessorKey: "interestRateYieldDelta",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div style={{ textAlign: "right" }}>{value !== null ? twoDecPriceFormat(value) : "N/A"}</div>;
                }
            },
            {
                header: "External account",
                accessorKey: "counterpartyName",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    if (fetchingReconcile) {
                        return "loading...";
                    }
                    return value || "N/A";
                }
            },
            {
                header: "Counterparty valuation",
                accessorKey: "counterpartyExposure",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    if (fetchingReconcile) {
                        return <div style={{ textAlign: "right" }}>loading...</div>;
                    }
                    // Change sign on Counterparty valuation
                    if (value === "Missing report") {
                        return <div style={{ textAlign: "right" }}>Missing report</div>;
                    }
                    return <div style={{ textAlign: "right" }}>{value !== null ? twoDecPriceFormat(-value) : "N/A"}</div>;
                }
            },
            {
                header: "Cpty diff",
                accessorKey: "cptyDiff",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div style={{ textAlign: "right" }}>{value !== null ? twoDecPriceFormat(value) : "N/A"}</div>;
                }
            },
            {
                header: "Currency",
                accessorKey: "currency"
            }
        ],
        [fetchingReconcile, updatedInstrumentIds]
    );

    if (error) {
        return (
            <div>
                <p>Error:</p>
                <pre>{JSON.stringify(error, null, 2)}</pre>
            </div>
        );
    }

    const handleSaveVolatility = async (newVolatility: number) => {
        if (!volatilityEdit) return;

        const instrument = volatilityEdit.instrument;
        if (!instrument || !instrument.model || !instrument.model.legs) return;

        // Clone the entire instrument
        const updatedInstrument = cloneDeep(instrument);

        // Clone the model and update the volatility for the SwaptionLeg
        const updatedModel = cloneDeep(updatedInstrument.model);
        const swaptionLegIndex = updatedModel.legs.findIndex((leg: any) => leg._t === "SwaptionLeg");

        if (swaptionLegIndex === -1) return;

        // Update the volatility for the SwaptionLeg
        updatedModel.legs[swaptionLegIndex] = {
            ...updatedModel.legs[swaptionLegIndex],
            volatility: newVolatility
        };

        // Update the model in the cloned instrument
        updatedInstrument.model = updatedModel;

        // Clean legs to remove __typename and underlyingInstrument
        const cleanedLegs = updatedModel.legs.map((leg: any) => {
            const { __typename, underlyingInstrument: _underlyingInstrument, cashFlows, ...cleanedLeg } = leg;
            // Clean cashFlows to remove __typename
            const cleanedCashFlows = cashFlows?.map((cashFlow: any) => {
                const { __typename: _cfTypename, ...cleanedCashFlow } = cashFlow;
                return cleanedCashFlow;
            });
            return {
                ...cleanedLeg,
                ...(cleanedCashFlows && { cashFlows: cleanedCashFlows })
            };
        });

        // Prepare the update input with the entire instrument
        // Spread all instrument properties and override with the updated model
        const updateInput: any = {
            _id: volatilityEdit.instrumentId,
            ...updatedInstrument,
            model: {
                _t: updatedModel._t,
                quoteCurrency: updatedModel.quoteCurrency,
                quoteType: updatedModel.quoteType,
                legs: cleanedLegs
            }
        };

        // Remove fields that should not be posted
        delete updateInput.longName;
        delete updateInput.modelType;
        delete updateInput.name;
        delete updateInput.__typename;

        const result = await updateInstrumentMutation({ input: updateInput });
        if (result.error) {
            throw result.error;
        }

        // Show success message
        setSaveMessage({ type: "success", text: "Volatility updated successfully!" });

        // Mark this instrument as updated
        if (volatilityEdit) {
            setUpdatedInstrumentIds((prev) => new Set(prev).add(volatilityEdit.instrumentId));

            // Update the oldVolatility in state so popup shows the new value
            setVolatilityEdit({
                ...volatilityEdit,
                oldVolatility: newVolatility
            });
        }

        // Clear message after 3 seconds
        setTimeout(() => {
            setSaveMessage(null);
        }, 3000);
    };

    return (
        <Page className="swaptionspage">
            <div>
                <h4>Swaptions</h4>
                <div className="row mb-2">
                    <div className="col-12 col-sm-4">
                        <DateForm defaultDateString={date} dateName={"date"}></DateForm>
                    </div>
                </div>
                {fetching ? <p>Loading...</p> : <ReactTable columns={columns} data={swaptionRows} />}
            </div>
            {volatilityEdit !== null && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 1000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        paddingLeft: "600px"
                    }}
                    onClick={() => {
                        setVolatilityEdit(null);
                        setSaveMessage(null);
                    }}
                >
                    <div
                        ref={modalRef}
                        style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            boxShadow: "0px 25.6px 57.6px 0px #00000038, 0px 4.8px 14.4px 0px #0000002e",
                            width: "280px",
                            maxWidth: "90%",
                            maxHeight: "65vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            position: "absolute",
                            left: modalPosition ? `${modalPosition.x}px` : "50%",
                            top: modalPosition ? `${modalPosition.y}px` : "50%",
                            transform: modalPosition ? "none" : "translate(-50%, -50%)",
                            cursor: isDragging ? "grabbing" : "default"
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "16px 20px",
                                borderBottom: "1px solid #e0e0e0",
                                cursor: "grab",
                                userSelect: "none"
                            }}
                            onMouseDown={handleDragStart}
                        >
                            <h2 style={{ margin: 0, fontSize: "18px" }}>Update Volatility</h2>
                            <button
                                onClick={() => {
                                    setVolatilityEdit(null);
                                    setSaveMessage(null);
                                }}
                                style={{
                                    background: "none",
                                    border: "none",
                                    fontSize: "24px",
                                    cursor: "pointer",
                                    padding: 0,
                                    width: "24px",
                                    height: "24px"
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ overflowY: "auto", padding: "20px", flex: 1 }}>
                            {volatilityEdit && (
                                <Formik
                                    key={volatilityEdit.instrumentId}
                                    initialValues={{ volatility: volatilityEdit.oldVolatility ?? 0 }}
                                    enableReinitialize={true}
                                    onSubmit={async (values, { setErrors }) => {
                                        try {
                                            setSaveMessage(null);
                                            await handleSaveVolatility(values.volatility);
                                        } catch (error: any) {
                                            if (error.graphQLErrors || error.networkError) {
                                                formikUrqlErrorFormater(error, setErrors);
                                                setSaveMessage({
                                                    type: "error",
                                                    text: "Error updating volatility. Please check the form for details."
                                                });
                                            } else {
                                                console.error("Error updating volatility:", error);
                                                setSaveMessage({ type: "error", text: "Error updating volatility: " + String(error) });
                                            }
                                        }
                                    }}
                                >
                                    {({ values, setFieldValue, isSubmitting }) => (
                                        <Form>
                                            <div style={{ marginBottom: "16px" }}>
                                                <label>Instrument:</label>
                                                <div style={{ marginTop: "4px", fontSize: "14px" }}>
                                                    <Link to={`/instruments/${volatilityEdit.instrumentId}`} target="_blank">
                                                        {volatilityEdit.instrumentName}
                                                    </Link>
                                                </div>
                                            </div>
                                            <div style={{ marginBottom: "16px" }}>
                                                <label>Old Value:</label>
                                                <div style={{ marginTop: "4px", fontSize: "14px", color: "#666" }}>
                                                    {volatilityEdit.oldVolatility !== null
                                                        ? priceFormat(volatilityEdit.oldVolatility)
                                                        : "N/A"}
                                                </div>
                                            </div>
                                            <div style={{ marginBottom: "16px" }}>
                                                <label>New Value:</label>
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline-secondary"
                                                        onClick={() => {
                                                            const current = values.volatility ?? 0;
                                                            const newValue = Math.max(0, current - 0.0001);
                                                            setFieldValue("volatility", Math.round(newValue * 10000) / 10000);
                                                        }}
                                                    >
                                                        ↓
                                                    </Button>
                                                    <div style={{ width: "50%" }}>
                                                        <NumberField name="volatility" label={null} className="" disabled={isSubmitting} />
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline-secondary"
                                                        onClick={() => {
                                                            const current = values.volatility ?? 0;
                                                            const newValue = current + 0.0001;
                                                            setFieldValue("volatility", Math.round(newValue * 10000) / 10000);
                                                        }}
                                                    >
                                                        ↑
                                                    </Button>
                                                </div>
                                            </div>
                                            <div style={{ marginBottom: "16px" }}>
                                                <label>Difference to existing:</label>
                                                <div style={{ marginTop: "4px", fontSize: "14px", color: "#666" }}>
                                                    {volatilityEdit.oldVolatility !== null
                                                        ? Math.round(
                                                              ((values.volatility ?? 0) - volatilityEdit.oldVolatility) * 10000 * 100
                                                          ) / 100
                                                        : "N/A"}
                                                </div>
                                            </div>
                                            {saveMessage && (
                                                <div
                                                    style={{
                                                        marginBottom: "16px",
                                                        padding: "8px 12px",
                                                        borderRadius: "4px",
                                                        backgroundColor: saveMessage.type === "success" ? "#d4edda" : "#f8d7da",
                                                        color: saveMessage.type === "success" ? "#155724" : "#721c24",
                                                        border: `1px solid ${saveMessage.type === "success" ? "#c3e6cb" : "#f5c6cb"}`
                                                    }}
                                                >
                                                    {saveMessage.text}
                                                </div>
                                            )}
                                            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                                                <Button type="button" variant="secondary" onClick={() => setVolatilityEdit(null)}>
                                                    Cancel
                                                </Button>
                                                <SubmitButton disabled={isSubmitting} label="Save" />
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Page>
    );
};
